import {useMemo, useState} from 'react'
import {useBazaarvoiceAPI} from '../context'
import {bvStorageFeedbackHelpfulnessKey, bvStorageFeedbackInappropriateKey} from '../utils'
import {API_ERROR_MESSAGE} from '../../constants'
import {useToast} from '../../hooks/use-toast'
import {useIntl} from 'react-intl'

/**
 * @param {'Review'} contentType
 * @param {string} contentId
 * @returns {{inappropriate: {submitting: boolean, submitted: boolean, submit: function(): Promise<void>}, helpful: {submitting: boolean, submitted: boolean, submit: function(boolean|'Positive'|'Negative'): Promise<void>, vote: 'Positive'|'Negative'|undefined}}}
 */
const useContentFeedback = ({contentType, contentId}) => {
    const {formatMessage} = useIntl()
    const toast = useToast()
    const {
        submittedFeedback,
        submitHelpfulnessVote: apiSubmitHelpfulnessVote,
        reportInappropriate: apiReportInappropriate
    } = useBazaarvoiceAPI()
    const [submittingHelpfulnessVote, setSubmittingHelpfulnessVote] = useState(false)
    const [submittingReportInappropriate, setSubmittingReportInappropriate] = useState(false)

    const submittedHelpfulnessVote = useMemo(
        () => submittedFeedback[bvStorageFeedbackHelpfulnessKey(contentType, contentId)],
        [contentType, contentId, submittedFeedback]
    )

    const isReportInappropriateSubmitted = useMemo(
        () =>
            submittedFeedback[bvStorageFeedbackInappropriateKey(contentType, contentId)] === 'true',
        [contentType, contentId, submittedFeedback]
    )

    return useMemo(() => {
        const isHelpfulnessVoteSubmitted = submittedHelpfulnessVote !== undefined
        return {
            helpful: {
                submitted: isHelpfulnessVoteSubmitted,
                vote: submittedHelpfulnessVote,
                submitting: submittingHelpfulnessVote,
                submit: async (vote) => {
                    if (isHelpfulnessVoteSubmitted) {
                        return
                    }
                    setSubmittingHelpfulnessVote(true)
                    try {
                        await apiSubmitHelpfulnessVote({contentType, contentId, vote})
                    } catch (e) {
                        console.error('Failed to submit helpfulness vote', e)
                        toast({
                            title: formatMessage(API_ERROR_MESSAGE),
                            status: 'error'
                        })
                    } finally {
                        setSubmittingHelpfulnessVote(false)
                    }
                }
            },
            inappropriate: {
                submitted: isReportInappropriateSubmitted,
                submitting: submittingReportInappropriate,
                submit: async () => {
                    if (isReportInappropriateSubmitted) {
                        return
                    }
                    setSubmittingReportInappropriate(true)
                    try {
                        await apiReportInappropriate({contentType, contentId})
                    } catch (e) {
                        console.error('Failed to report inappropriate content', e)
                        toast({
                            title: formatMessage(API_ERROR_MESSAGE),
                            status: 'error'
                        })
                    } finally {
                        setSubmittingReportInappropriate(false)
                    }
                }
            }
        }
    }, [
        submittedHelpfulnessVote,
        isReportInappropriateSubmitted,
        apiSubmitHelpfulnessVote,
        apiReportInappropriate,
        contentType,
        contentId,
        formatMessage,
        submittingHelpfulnessVote,
        submittingReportInappropriate,
        toast
    ])
}

export default useContentFeedback
